/**
 * FP3-077 → FP3-085 — Bulk Student Actions modal.
 *
 * One modal, seven flows. Driven by the `kind` prop:
 *
 *   • "add-impact"      — FP3-085 — pick broad-domain Areas of Impact,
 *                         add to selected students' PRIMARY eligibility
 *                         entry (writes to
 *                         `eligibility_categories[*].subcategories`).
 *   • "remove-impact"   — FP3-085 — pick Areas of Impact, remove from
 *                         EVERY eligibility entry on the selected
 *                         students.
 *   • "add-domains"     — FP3-077 — kept for backward compat with any
 *                         external caller. Writes to `service_areas`.
 *                         Not surfaced in the new Students-page UI.
 *   • "remove-domains"  — Same, remove variant.
 *   • "add-provider"    — pick provider + role.
 *   • "remove-provider" — pick provider, remove from selected.
 *   • "clear-providers" — clear ALL providers (destructive).
 *
 * Every flow runs a dry-run preview first, surfaces counts to the
 * admin, and only writes when the operator clicks Apply.
 */
import { useEffect, useMemo, useState } from "react";
import {
  X,
  Warning,
  WarningCircle,
  CheckCircle,
  Spinner,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

// Mirrors backend services_config.DOMAINS so the UI doesn't depend on
// an extra round-trip. Order matches the backend canonical order.
const DOMAINS = [
  { key: "speech_language",  label: "Speech-Language" },
  { key: "ot",               label: "Occupational Therapy" },
  { key: "pt",               label: "Physical Therapy" },
  { key: "social_behavior",  label: "Social-Emotional / Behavior" },
  { key: "academics",        label: "Academics" },
  { key: "hearing_vision",   label: "Hearing / Vision" },
  { key: "transition",       label: "Transition / Independent Living" },
  { key: "health_nursing",   label: "Health / Nursing" },
  { key: "support_access",   label: "Support / Access Services" },
  { key: "enrichment_other", label: "Enrichment / Other" },
];

const TITLE_BY_KIND = {
  "add-impact":      "Add Areas of Impact",
  "remove-impact":   "Remove Areas of Impact",
  "add-domains":     "Add service domains",
  "remove-domains":  "Remove service domains",
  "add-provider":    "Add provider",
  "remove-provider": "Remove provider",
  "clear-providers": "Clear all providers",
};

const ROLE_LABELS = {
  team:      "Add as team member",
  primary:   "Make Primary Provider",
  eval_team: "Add to Eval team",
};

export default function BulkActionsModal({
  open,
  onClose,
  kind,
  studentIds,
  students,
  providers,
  onApplied,
}) {
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [providerId, setProviderId] = useState("");
  const [role, setRole] = useState("team");
  const [confirmAssistant, setConfirmAssistant] = useState(false);
  const [confirmClearPrimary, setConfirmClearPrimary] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [applying, setApplying] = useState(false);

  // Reset state every time the modal opens with a new kind.
  useEffect(() => {
    if (open) {
      setSelectedDomains([]);
      setProviderId("");
      setRole("team");
      setConfirmAssistant(false);
      setConfirmClearPrimary(false);
      setPreview(null);
      setPreviewError("");
    }
  }, [open, kind]);

  const selectedProvider = useMemo(
    () => (providers || []).find((p) => p.id === providerId) || null,
    [providers, providerId]
  );

  const providerIsAssistant = useMemo(() => {
    if (!selectedProvider) return false;
    return (selectedProvider.provider_role_type || "primary") === "assistant";
  }, [selectedProvider]);

  // Selection summary chips.
  const studentCount = (studentIds || []).length;
  const studentSamplePreview = useMemo(
    () => (students || []).slice(0, 5),
    [students]
  );

  const buildPayload = (dryRun) => {
    if (kind === "add-impact") {
      // FP3-085 — Writes to eligibility_categories[primary].subcategories.
      return {
        endpoint: api.bulkImpactAdd,
        body: {
          student_ids: studentIds,
          impact_keys: selectedDomains,
          action: "add",
          dry_run: dryRun,
        },
      };
    }
    if (kind === "remove-impact") {
      // FP3-085 — Removes from EVERY eligibility entry so a
      // secondary entry can't keep the student matched.
      return {
        endpoint: api.bulkImpactRemove,
        body: {
          student_ids: studentIds,
          impact_keys: selectedDomains,
          action: "remove",
          dry_run: dryRun,
        },
      };
    }
    if (kind === "add-domains") {
      return {
        endpoint: api.bulkServiceDomains,
        body: {
          student_ids: studentIds,
          domain_keys: selectedDomains,
          action: "add",
          dry_run: dryRun,
        },
      };
    }
    if (kind === "remove-domains") {
      return {
        endpoint: api.bulkServiceDomains,
        body: {
          student_ids: studentIds,
          domain_keys: selectedDomains,
          action: "remove",
          dry_run: dryRun,
        },
      };
    }
    if (kind === "add-provider") {
      return {
        endpoint: api.bulkAddProvider,
        body: {
          student_ids: studentIds,
          provider_id: providerId,
          role,
          confirm_assistant_as_primary: confirmAssistant,
          dry_run: dryRun,
        },
      };
    }
    if (kind === "remove-provider") {
      return {
        endpoint: api.bulkRemoveProvider,
        body: {
          student_ids: studentIds,
          provider_id: providerId,
          confirm_clear_primary: confirmClearPrimary,
          dry_run: dryRun,
        },
      };
    }
    // clear-providers
    return {
      endpoint: api.bulkRemoveProvider,
      body: {
        student_ids: studentIds,
        clear_all: true,
        confirm_clear_primary: confirmClearPrimary,
        dry_run: dryRun,
      },
    };
  };

  const validateBeforeFire = () => {
    if (studentCount === 0) {
      setPreviewError("Select at least one student first.");
      return false;
    }
    if (studentCount > 500) {
      setPreviewError(
        `Bulk actions are capped at 500 students per call. You selected ${studentCount}. ` +
        `Narrow the selection and retry.`
      );
      return false;
    }
    if ((kind === "add-domains" || kind === "remove-domains" || kind === "add-impact" || kind === "remove-impact") && selectedDomains.length === 0) {
      // FP3-085 — Impact + legacy domain kinds share the same picker.
      setPreviewError(
        kind === "add-impact" || kind === "remove-impact"
          ? "Pick at least one Area of Impact."
          : "Pick at least one service domain."
      );
      return false;
    }
    if ((kind === "add-provider" || kind === "remove-provider") && !providerId) {
      setPreviewError("Pick a provider.");
      return false;
    }
    setPreviewError("");
    return true;
  };

  const runPreview = async () => {
    if (!validateBeforeFire()) return;
    setPreviewLoading(true);
    setPreview(null);
    try {
      const { endpoint, body } = buildPayload(true);
      const r = await endpoint(body);
      setPreview(r.data);
    } catch (err) {
      setPreviewError(
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Could not preview the bulk action."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    if (!validateBeforeFire()) return;
    setApplying(true);
    try {
      const { endpoint, body } = buildPayload(false);
      const r = await endpoint(body);
      if (r.data?.needs_confirmation) {
        // Apply was blocked by a confirmation gate. Stay open so
        // the operator can toggle the confirmation checkbox.
        setPreview(r.data);
        toast.message(r.data.message || "Confirmation required.");
        return;
      }
      toast.success(
        `Updated ${r.data?.updated ?? 0} student(s) · ${
          r.data?.already_satisfied ?? r.data?.skipped ?? 0
        } unchanged.`
      );
      if (onApplied) onApplied(r.data);
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Could not apply the bulk action."
      );
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  const isDomainsKind = kind === "add-domains" || kind === "remove-domains";
  // FP3-085 — Impact kinds share the same picker layout.
  const isImpactKind = kind === "add-impact" || kind === "remove-impact";
  const isAddProvider = kind === "add-provider";
  const isRemoveProvider = kind === "remove-provider";
  const isClear = kind === "clear-providers";

  // Confirmation gates that block Apply.
  const blockedNeedsAssistant =
    isAddProvider && role === "primary" && providerIsAssistant && !confirmAssistant;
  const previewBlockedByGate = preview?.needs_confirmation;
  const applyEnabled =
    !applying &&
    studentCount > 0 &&
    !blockedNeedsAssistant &&
    !previewBlockedByGate;

  return (
    <div
      data-testid="bulk-actions-modal"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-[#E5E3DB] flex items-center justify-between bg-[#FBFAF6]">
          <div>
            <h2 className="text-[15px] font-semibold text-[#1F2937] heading-font">
              {TITLE_BY_KIND[kind]}
            </h2>
            <p
              className="text-[11.5px] text-[#6B7280] mt-0.5"
              data-testid="bulk-actions-modal-student-count"
            >
              {studentCount} student{studentCount === 1 ? "" : "s"} selected
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="bulk-actions-modal-close"
            className="p-1 rounded-md hover:bg-[#F3F1EC]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto space-y-4 flex-1">
          {isClear && (
            <div className="rounded-md bg-[#FBEEEA] border border-[#E8CFC8] p-3 text-[12px] text-[#7A3A2C]">
              <p className="font-semibold mb-1 flex items-center gap-1.5">
                <Warning size={14} weight="duotone" />
                Strong confirmation required
              </p>
              <p>
                This removes provider assignments from selected students
                but does not delete students, providers, goals, notes,
                documents, sessions, imported data, or history.
              </p>
            </div>
          )}

          {/* Selected sample */}
          {studentSamplePreview.length > 0 && (
            <div>
              <Label className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
                Selected sample ({Math.min(5, studentCount)} of {studentCount})
              </Label>
              <div
                className="mt-1 text-[11.5px] text-[#1F2937]"
                data-testid="bulk-actions-sample-list"
              >
                {studentSamplePreview.map((s) => (
                  <span
                    key={s.id}
                    className="inline-block mr-2 mb-1 px-2 py-0.5 rounded-full bg-[#F3F1EC] border border-[#E5E3DB]"
                  >
                    {(s.first_name || "") + " " + (s.last_name || "")}
                  </span>
                ))}
                {studentCount > 5 && (
                  <span className="text-[11px] text-[#6B7280]">
                    +{studentCount - 5} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Domain picker — used for both legacy Service Domains and
              FP3-085 Areas of Impact kinds. */}
          {(isDomainsKind || isImpactKind) && (
            <div>
              <Label className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
                {isImpactKind
                  ? `Area${selectedDomains.length === 1 ? "" : "s"} of Impact`
                  : `Service domain${selectedDomains.length === 1 ? "" : "s"}`}
              </Label>
              <div
                className="mt-1.5 grid grid-cols-2 gap-2"
                data-testid="bulk-domain-grid"
              >
                {DOMAINS.map((d) => {
                  const checked = selectedDomains.includes(d.key);
                  return (
                    <label
                      key={d.key}
                      data-testid={`bulk-domain-row-${d.key}`}
                      className={`flex items-center gap-2 px-2.5 py-2 rounded-md border cursor-pointer ${
                        checked
                          ? "border-[#1E3F33] bg-[#E8F0EB]"
                          : "border-[#E5E3DB] hover:bg-[#FBFAF6]"
                      }`}
                    >
                      <Checkbox
                        data-testid={`bulk-domain-checkbox-${d.key}`}
                        checked={checked}
                        onCheckedChange={(c) => {
                          setSelectedDomains((prev) =>
                            c
                              ? Array.from(new Set([...prev, d.key]))
                              : prev.filter((k) => k !== d.key)
                          );
                          setPreview(null);
                        }}
                      />
                      <span className="text-[12px] text-[#1F2937]">{d.label}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[10.5px] text-[#6B7280] mt-1.5" data-testid="bulk-domain-helper">
                {isImpactKind
                  ? "Adding an Area of Impact updates the student's eligibility profile only. It does not create services, provider assignments, goals, schedules, commitments, sessions, minutes, reports, or fidelity expectations."
                  : "Tagging a domain only updates the student's service-area metadata. It does not create service commitments, minutes, sessions, schedules, goals, or fidelity expectations."}
              </p>
            </div>
          )}

          {/* Provider picker */}
          {(isAddProvider || isRemoveProvider) && (
            <div>
              <Label className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
                Provider
              </Label>
              <select
                data-testid="bulk-provider-select"
                value={providerId}
                onChange={(e) => {
                  setProviderId(e.target.value);
                  setPreview(null);
                  setConfirmAssistant(false);
                }}
                className="mt-1 block w-full h-9 border border-[#E5E3DB] rounded-md px-2 text-[12.5px] bg-white"
              >
                <option value="">— Pick a provider —</option>
                {(providers || []).map((p) => {
                  const isAsst =
                    (p.provider_role_type || "primary") === "assistant";
                  const tag = isAsst ? " · assistant" : "";
                  const last = p.last_name || "";
                  const first = p.first_name || "";
                  const role = p.role || "";
                  return (
                    <option key={p.id} value={p.id}>
                      {last && first ? `${last}, ${first}` : (last || first || "(unnamed)")}
                      {role ? ` — ${role}` : ""}
                      {tag}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Role picker (add provider only) */}
          {isAddProvider && (
            <div>
              <Label className="text-[10.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
                Role on each selected student
              </Label>
              <div
                className="mt-1.5 flex flex-wrap gap-2"
                data-testid="bulk-provider-role-group"
              >
                {Object.entries(ROLE_LABELS).map(([k, v]) => {
                  const checked = role === k;
                  return (
                    <label
                      key={k}
                      data-testid={`bulk-provider-role-${k}`}
                      className={`px-3 py-1.5 rounded-full border cursor-pointer text-[11.5px] ${
                        checked
                          ? "border-[#1E3F33] bg-[#E8F0EB] text-[#1E3F33] font-semibold"
                          : "border-[#E5E3DB] text-[#1F2937] hover:bg-[#FBFAF6]"
                      }`}
                    >
                      <input
                        type="radio"
                        name="bulk-provider-role"
                        value={k}
                        checked={checked}
                        onChange={() => {
                          setRole(k);
                          setPreview(null);
                          setConfirmAssistant(false);
                        }}
                        className="sr-only"
                      />
                      {v}
                    </label>
                  );
                })}
              </div>
              {role === "primary" && providerIsAssistant && (
                <div
                  data-testid="bulk-assistant-primary-warning"
                  className="mt-2 rounded-md bg-[#FBF3E4] border border-[#E0CFA8] px-3 py-2 text-[11.5px] text-[#7A6A2C]"
                >
                  <p className="font-semibold flex items-center gap-1.5">
                    <Warning size={12} weight="duotone" />
                    This provider is an assistant.
                  </p>
                  <label className="mt-1.5 flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      data-testid="bulk-confirm-assistant-primary"
                      checked={confirmAssistant}
                      onCheckedChange={(c) => setConfirmAssistant(!!c)}
                    />
                    <span className="text-[11.5px]">
                      I understand and want to set this assistant as Primary Provider anyway.
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Clear-primary confirmation (remove + clear flows) */}
          {(isRemoveProvider || isClear) && (
            <div className="rounded-md border border-[#E5E3DB] bg-[#FBFAF6] p-2.5">
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  data-testid="bulk-confirm-clear-primary"
                  checked={confirmClearPrimary}
                  onCheckedChange={(c) => setConfirmClearPrimary(!!c)}
                />
                <span className="text-[11.5px] text-[#1F2937]">
                  Yes — clear Primary Provider on selected students if this
                  action removes their Primary. (Required when the action
                  affects a Primary.)
                </span>
              </label>
            </div>
          )}

          {/* Preview area */}
          {previewError && (
            <div
              data-testid="bulk-actions-error"
              className="rounded-md bg-[#FBEEEA] border border-[#E8CFC8] px-3 py-2 text-[12px] text-[#7A3A2C] flex items-start gap-1.5"
            >
              <WarningCircle size={14} weight="duotone" className="mt-0.5 shrink-0" />
              <span>{previewError}</span>
            </div>
          )}

          {preview && !preview.needs_confirmation && (
            <div
              data-testid="bulk-actions-preview"
              className="rounded-md border border-[#D6E0DA] bg-[#F1F7F3] p-3"
            >
              <p className="text-[11.5px] font-semibold text-[#1E3F33] mb-1.5 flex items-center gap-1.5">
                <CheckCircle size={13} weight="duotone" />
                Preview (no changes written yet)
              </p>
              <div className="grid grid-cols-3 gap-2 text-[11.5px]">
                <Stat
                  label="Selected"
                  value={preview.matched ?? studentCount}
                  testid="bulk-preview-matched"
                />
                <Stat
                  label="Will update"
                  value={preview.updated ?? 0}
                  testid="bulk-preview-will-update"
                />
                <Stat
                  label={
                    preview.already_satisfied != null
                      ? "Already satisfied"
                      : "Skipped"
                  }
                  value={
                    preview.already_satisfied ?? preview.skipped ?? 0
                  }
                  testid="bulk-preview-already"
                />
              </div>
              {(preview.primaries_affected || 0) > 0 && (
                <p
                  data-testid="bulk-preview-primaries-warning"
                  className="mt-2 text-[11px] text-[#7A6A2C] bg-[#FBF3E4] border border-[#E0CFA8] rounded px-2 py-1"
                >
                  <Warning size={11} className="inline mb-0.5 mr-1" />
                  This will affect Primary Provider on{" "}
                  {preview.primaries_affected} student(s).
                </p>
              )}
            </div>
          )}

          {preview?.needs_confirmation && (
            <div
              data-testid="bulk-actions-needs-confirm"
              className="rounded-md bg-[#FBF3E4] border border-[#E0CFA8] px-3 py-2 text-[12px] text-[#7A6A2C]"
            >
              <p className="font-semibold flex items-center gap-1.5">
                <Warning size={13} weight="duotone" />
                Confirmation required
              </p>
              <p className="mt-1">{preview.message}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#E5E3DB] bg-[#FBFAF6] flex items-center justify-between">
          <Button
            type="button"
            data-testid="bulk-actions-preview-btn"
            variant="outline"
            disabled={previewLoading}
            onClick={runPreview}
            className="h-8 px-3 text-[11.5px] border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E8F0EB]"
          >
            {previewLoading ? (
              <Spinner size={12} className="mr-1.5 animate-spin" />
            ) : null}
            Preview
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="bulk-actions-cancel"
              className="h-8 px-3 text-[11.5px] border-[#E5E3DB] text-[#1F2937] hover:bg-[#F3F1EC]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="bulk-actions-apply"
              onClick={runApply}
              disabled={!applyEnabled}
              className={`h-8 px-4 text-[11.5px] text-white ${
                isClear
                  ? "bg-[#7A3A2C] hover:bg-[#62301F]"
                  : "bg-[#1E3F33] hover:bg-[#173127]"
              } disabled:opacity-50`}
            >
              {applying ? (
                <Spinner size={12} className="mr-1.5 animate-spin" />
              ) : null}
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-md bg-white border border-[#D6E0DA] px-2.5 py-1.5"
    >
      <p className="text-[9.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
        {label}
      </p>
      <p className="text-[16px] font-semibold text-[#1F2937] leading-tight">
        {value}
      </p>
    </div>
  );
}
